import { Devices } from '@mui/icons-material';
import { Link } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { CompanyEquipmentType } from '~/data/equipment';
import { useGetCompanyEquipment } from '~/data/equipment/useGetCompanyEquipment';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportViewLayout,
} from '~/scenes/PortalModules/Analytics/common';
import { getCompanyEquipmentAttachmentUrl } from '~/utils/helper';
import { getEnumName } from '~/utils/helpers/enum';

export const CompanyEquipmentReport = () => {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();

  const { getEnums } = useAllEnums();
  const { equipmentTypeEnums } = useMemo(() => {
    return {
      equipmentTypeEnums: getEnums(EnumListKey.EQUIPMENT_TYPE),
    };
  }, [getEnums]);

  const { data, isLoading } = useGetCompanyEquipment();

  const columns = useMemo((): GridColDef<CompanyEquipmentType>[] => {
    return [
      {
        field: 'fComEquipmentInvNumber',
        headerName: t('laEquipmentNumber'),
        width: 150,
      },
      {
        field: 'fComEquipmentItem',
        headerName: t('laEquipmentItem'),
        width: 300,
      },
      {
        field: 'fComEquipmentType',
        headerName: t('laType'),
        width: 150,
        valueGetter: ({ value }) => getEnumName(equipmentTypeEnums, value),
      },
      {
        field: 'fComEquipmentAttachmentName',
        headerName: t('laAttachment'),
        width: 300,
        flex: 1,
        renderCell: ({ value, row }) => {
          return (
            <Link
              href={getCompanyEquipmentAttachmentUrl(
                row.fComEquipmentId,
                row.fComEquipmentAttachmentId,
              )}
              target={'_blank'}
            >
              {value}
            </Link>
          );
        },
      },
    ];
  }, [t, equipmentTypeEnums]);

  return (
    <ReportViewLayout
      heading={t('laCompanyEquipment')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout>
              <ReportDatasetRowCount
                count={data.length}
                description={t('equipment')}
                icon={<Devices />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility apiRef={apiRef} isLoading={isLoading} />
              <ReportDataExport
                title={t('laCompanyEquipment')}
                apiRef={apiRef}
                isLoading={isLoading}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={data}
        loading={isLoading}
        density={'compact'}
        getRowId={(row) => row.fComEquipmentId}
      />
    </ReportViewLayout>
  );
};
